import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const Player = () => {
  const [searchParams] = useSearchParams();
  const [mediaGroup, setMediaGroup] = useState(null);
  const [media, setMedia] = useState([]);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cacheStatus, setCacheStatus] = useState({});
  const [allCached, setAllCached] = useState(false);

  const secret = searchParams.get('sec');
  const videoRef = useRef(null);
  const imageTimerRef = useRef(null);

  useEffect(() => {
    if (!secret) {
      setError('No secret parameter provided');
      setLoading(false);
      return;
    }

    fetchMediaGroupData();
  }, [secret]);

  // Cache all media files
  useEffect(() => {
    if (media.length > 0) {
      cacheAllMedia();
    }
  }, [media]);

  // Auto-advance media
  useEffect(() => {
    if (!allCached || media.length === 0) return;

    const currentMedia = media[currentMediaIndex];
    if (!currentMedia) return;

    // Clear any existing timer
    if (imageTimerRef.current) {
      clearTimeout(imageTimerRef.current);
      imageTimerRef.current = null;
    }

    if (currentMedia.type === 'image') {
      // For images, convert duration from seconds to milliseconds
      // If duration is null or 0, use default of 5 seconds
      const durationInSeconds = currentMedia.duration || 5;
      const durationInMs = durationInSeconds * 1000;
      
      console.log(`Image ${currentMedia.name} will display for ${durationInSeconds} seconds (${durationInMs}ms)`);
      
      imageTimerRef.current = setTimeout(() => {
        console.log(`Advancing from image ${currentMedia.name} after ${durationInSeconds} seconds`);
        advanceToNext();
      }, durationInMs);
    } else if (currentMedia.type === 'video' && videoRef.current) {
      // Play with audio; if the browser's autoplay policy blocks it, fall back to muted playback
      const video = videoRef.current;
      video.muted = false;
      video.volume = 1;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.warn(`Autoplay with audio blocked for ${currentMedia.name}, playing muted:`, err);
          video.muted = true;
          video.play().catch((e) => console.error(`Muted playback failed for ${currentMedia.name}:`, e));
        });
      }
    }
    // For videos, advancement is handled in the onEnded event

    return () => {
      if (imageTimerRef.current) {
        clearTimeout(imageTimerRef.current);
        imageTimerRef.current = null;
      }
    };
  }, [currentMediaIndex, allCached, media]);

  const fetchMediaGroupData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`/api/media/public/player/${secret}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Media group not found or public access is disabled');
        }
        throw new Error('Failed to load media group');
      }

      const data = await response.json();
      setMediaGroup(data.mediaGroup);
      setMedia(data.media || []);
    } catch (error) {
      console.error('Error fetching media group:', error);
      setError(error.message || 'Failed to load media content');
    } finally {
      setLoading(false);
    }
  };

  const cacheAllMedia = async () => {
    console.log('Starting to cache all media files...');
    const cachePromises = media.map(async (item, index) => {
      try {
        // For images, we can preload them
        if (item.type === 'image') {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              setCacheStatus(prev => ({ ...prev, [index]: 'cached' }));
              console.log(`Cached image: ${item.name}`);
              resolve();
            };
            img.onerror = () => {
              setCacheStatus(prev => ({ ...prev, [index]: 'error' }));
              console.error(`Failed to cache image: ${item.name}`);
              reject();
            };
            img.src = item.url;
          });
        } 
        // For videos, we'll mark as ready since they'll be loaded when needed
        else if (item.type === 'video') {
          setCacheStatus(prev => ({ ...prev, [index]: 'cached' }));
          console.log(`Ready for video: ${item.name}`);
          return Promise.resolve();
        }
      } catch (error) {
        setCacheStatus(prev => ({ ...prev, [index]: 'error' }));
        console.error(`Error caching ${item.name}:`, error);
      }
    });

    try {
      await Promise.all(cachePromises);
      setAllCached(true);
      console.log('All media cached successfully');
    } catch (error) {
      console.error('Some media failed to cache, but continuing anyway');
      setAllCached(true); // Continue even if some items failed
    }
  };

  const advanceToNext = () => {
    const nextIndex = currentMediaIndex >= media.length - 1 ? 0 : currentMediaIndex + 1;
    console.log(`Advancing from media ${currentMediaIndex + 1} (${media[currentMediaIndex]?.name}) to media ${nextIndex + 1} (${media[nextIndex]?.name})`);
    setCurrentMediaIndex(nextIndex);
  };

  const handleVideoEnded = () => {
    const currentMedia = media[currentMediaIndex];
    console.log(`Video ${currentMedia?.name} ended, advancing to next media`);
    advanceToNext();
  };

  const handleVideoError = (e) => {
    const currentMedia = media[currentMediaIndex];
    console.error(`Video error for ${currentMedia?.name}:`, e);
    // Skip to next media if video fails
    advanceToNext();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-6"></div>
          <p className="text-white text-xl">Loading media content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-400 mb-6">
            <svg className="h-20 w-20 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.346 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-white text-3xl font-bold mb-4">Content Not Available</h1>
          <p className="text-gray-300 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  if (!media || media.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-gray-400 mb-6">
            <svg className="h-20 w-20 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h1 className="text-white text-3xl font-bold mb-4">{mediaGroup?.name || 'Media Group'}</h1>
          <p className="text-gray-300 text-lg">No media content available</p>
        </div>
      </div>
    );
  }

  // Show caching progress
  if (!allCached) {
    const cachedCount = Object.values(cacheStatus).filter(status => status === 'cached').length;
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse mb-6">
            <div className="w-32 h-32 border-4 border-white rounded-full mx-auto flex items-center justify-center">
              <span className="text-white text-2xl font-bold">{Math.round((cachedCount / media.length) * 100)}%</span>
            </div>
          </div>
          <p className="text-white text-xl mb-2">Caching media files...</p>
          <p className="text-gray-300">{cachedCount} of {media.length} files cached</p>
        </div>
      </div>
    );
  }

  const currentMedia = media[currentMediaIndex];

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      {/* Full screen media display */}
      <div className="w-full h-full flex items-center justify-center">
        {currentMedia.type === 'image' ? (
          <img
            src={currentMedia.url}
            alt={currentMedia.name}
            className="max-w-full max-h-full object-contain"
            style={{ width: '100vw', height: '100vh', objectFit: 'contain' }}
            onLoad={() => console.log(`Image ${currentMedia.name} loaded and displayed`)}
            onError={() => {
              console.error(`Image ${currentMedia.name} failed to load, skipping to next`);
              advanceToNext();
            }}
          />
        ) : currentMedia.type === 'video' ? (
          <video
            ref={videoRef}
            src={currentMedia.url}
            autoPlay
            className="max-w-full max-h-full object-contain"
            style={{ width: '100vw', height: '100vh', objectFit: 'contain' }}
            onEnded={handleVideoEnded}
            onError={handleVideoError}
            onLoadStart={() => console.log(`Video ${currentMedia.name} started loading`)}
            onCanPlay={() => console.log(`Video ${currentMedia.name} can play`)}
            onPlay={() => console.log(`Video ${currentMedia.name} started playing`)}
            playsInline
            preload="auto"
            loop={false}
          />
        ) : (
          <div className="text-white text-center p-8">
            <p className="text-2xl mb-4">Unsupported media type: {currentMedia.type}</p>
            <p className="text-gray-400">{currentMedia.name}</p>
          </div>
        )}
      </div>

      {/* Clean display - no visible UI elements */}
    </div>
  );
};

export default Player;
